import React, { Fragment, useLayoutEffect, useRef, useState } from "react";
import moment, { Moment } from 'moment';
import { useTranslation } from 'react-i18next';
import { TextField } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import Event from '@mui/icons-material/Event';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';

const DATE_FORMAT = 'MM-DD-YYYY';

// Form values are ISO strings (see the date field `normalize`); x-date-pickers only accepts moment objects or null.
const toMoment = (value: any): Moment | null => {
  if (!value) {
    return null;
  }
  return moment.isMoment(value) ? value : moment(value, [moment.ISO_8601, DATE_FORMAT]);
};

const formatMoment = (value: Moment | null): string => (value?.isValid() ? value.format(DATE_FORMAT) : '');

// Keeps up to 8 digits and adds the dashes: "0717", "07-17-1977" and "07/17/1977" become "07-17" and "07-17-1977".
const maskDate = (text: string): string => {
  const digits = text.replace(/\D/g, '').slice(0, 8);
  return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4)].filter(Boolean).join('-');
};

// Index in `masked` just after its `digitCount`-th digit, so the caret stays put when dashes are added or removed.
const caretAfterDigits = (masked: string, digitCount: number): number => {
  if (digitCount === 0) {
    return 0;
  }
  let seen = 0;
  for (let i = 0; i < masked.length; i += 1) {
    if (/\d/.test(masked[i])) {
      seen += 1;
      if (seen === digitCount) {
        return i + 1;
      }
    }
  }
  return masked.length;
};

/**
 * The picker's typing field: a plain masked text input, like the v4 KeyboardDatePicker, instead of the x-date-pickers
 * sectioned field. The sectioned field keeps its own month/day/year model and drops input that doesn't match it, so
 * clearing the field and typing a whole date (what Datadog synthetics do), pasting, or autofill lost the value. A
 * plain input accepts all of those. The calendar button and popup are unchanged.
 */
const MaskedDateField = (props: any) => {
  const { autoFocus, className, disabled, focused, id, InputProps, inputRef, label, name, onChange, readOnly, slotProps, sx, value } = props;
  const textFieldProps = slotProps?.textField ?? {};

  const [text, setText] = useState(() => formatMoment(value));
  // The date this field last showed, as text. A `value` that formats differently came from outside (a calendar pick,
  // or the form being prefilled or reset), so the text follows it; partial typing leaves `value` invalid ('') and is
  // left alone.
  const syncedRef = useRef(formatMoment(value));
  const caretRef = useRef<number | null>(null);
  const elementRef = useRef<HTMLInputElement | null>(null);

  const formatted = formatMoment(value);
  if (formatted !== syncedRef.current) {
    syncedRef.current = formatted;
    setText(formatted);
  }

  useLayoutEffect(() => {
    if (caretRef.current !== null && elementRef.current === document.activeElement) {
      elementRef.current.setSelectionRange(caretRef.current, caretRef.current);
    }
    caretRef.current = null;
  }, [text]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    const digitsBeforeCaret = raw.slice(0, event.target.selectionStart ?? raw.length).replace(/\D/g, '').length;
    const masked = maskDate(raw);
    caretRef.current = caretAfterDigits(masked, digitsBeforeCaret);
    setText(masked);

    const parsed = masked.length === DATE_FORMAT.length ? moment(masked, DATE_FORMAT, true) : moment.invalid();
    syncedRef.current = formatMoment(parsed);
    onChange(masked ? parsed : null, { validationError: parsed.isValid() ? null : 'invalidDate' });
  };

  return (
    <TextField
      {...textFieldProps}
      autoFocus  = {autoFocus}
      className  = {className}
      disabled   = {disabled}
      focused    = {focused}
      id         = {id}
      InputProps = {InputProps}
      inputProps = {{
        ...textFieldProps.inputProps,
        autoComplete: 'off',
        inputMode   : 'numeric',
        placeholder : DATE_FORMAT,
        readOnly,
      }}
      inputRef   = {(element: HTMLInputElement | null) => {
        elementRef.current = element;
        if (typeof inputRef === 'function') {
          inputRef(element);
        } else if (inputRef) {
          inputRef.current = element;
        }
      }}
      label      = {label}
      name       = {name}
      onChange   = {handleChange}
      sx         = {sx}
      value      = {text}
    />
  );
};

export const DateInput = (props) => {
  const { meta, input } = props;
  const { touched, error } = meta;
  const { onBlur, onChange, value } = input;
  const shouldShowError = touched && !!error;

  const { t } = useTranslation();

  return (
    <Fragment>
      <DesktopDatePicker
        format     = {DATE_FORMAT}
        label      = {props.label}
        localeText = {{
          cancelButtonLabel: t('button.datePicker.cancel', { defaultValue: 'Cancel' }),
          okButtonLabel    : t('button.datePicker.submit', { defaultValue: 'OK' }),
        }}
        onChange   = {(value: Moment | null) => {
          if (value?.isValid()) {
            value.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
          }
          onChange(value);
        }}
        slots      = {{
          field         : MaskedDateField,
          leftArrowIcon : KeyboardArrowLeft,
          openPickerIcon: Event,
          rightArrowIcon: KeyboardArrowRight,
        }}
        slotProps  = {{
          // Match the v4 KeyboardDatePicker icon button: 48px with 12px padding, inside the field edge.
          openPickerButton: {
            edge: false,
            sx  : { padding: '12px' },
          },
          textField: {
            error     : shouldShowError,
            fullWidth : true,
            helperText: touched && error,
            // Only mark the field touched. The value comes from the picker's onChange: a moment, an invalid moment
            // while partly typed, or null when empty.
            onBlur    : () => onBlur(),
            onFocus   : props.input.onFocus,
            variant   : 'standard',
          },
        }}
        value      = {toMoment(value)}
        {...props.datePickerProps}
      />
    </Fragment>
  );
};
